import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'sonner';
import App from './App';
import { OfflineProvider } from '@/contexts/OfflineContext';
import { createAppQueryClient } from '@/lib/query-client';
import { initDesktopApi } from '@/lib/desktop';
import { reloadOnceForChunkError } from '@/lib/chunk-error';
import './index.css';

// Vite's preload wrapper reports a chunk the current deploy no longer
// serves (stale tab) as this event: reload, guarded against a loop
// (chunk-error.ts).
window.addEventListener('vite:preloadError', () => {
  reloadOnceForChunkError();
});

// Register the PWA service worker (built by vite-plugin-pwa from src/sw.ts)
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register(
      import.meta.env.MODE === 'production' ? '/sw.js' : '/dev-sw.js?dev-sw'
    ).catch(() => {
      // SW registration failed — app works fine without it
    });
  });
}

// Cache identity is scoped to the active tenant — see @/lib/query-client.
const queryClient = createAppQueryClient();

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Élément root non trouvé');
}

// Desktop (Electron): resolve the embedded API URL via IPC before first
// render so the API client has its base URL. Resolves immediately (and
// never rejects) in a regular browser.
initDesktopApi().then(() => {
  createRoot(rootElement).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <OfflineProvider>
          <App />
        </OfflineProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            classNames: {
              error: 'bg-destructive text-destructive-foreground',
              success: 'bg-brand-green-700 text-white',
              warning: 'bg-brand-gold-500 text-black',
              info: 'bg-primary text-primary-foreground',
            },
          }}
        />
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </StrictMode>
  );
});
